<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { Ref } from "vue";
import { isMobile } from "~/utils/constants";

interface IPopOverProps {
	modelValue: boolean;
	contentKey: string;
	title?: string;
	teleportElement?: HTMLElement;
	disableClose?: boolean;
	styles?: {
		trigger?: string | string[];
		content?: string | string[];
		header?: string | string[];
		modelWrapper?: string | string[];
		wrapper?: string | string[];
		contentInner?: string | string[];
		modalContent?: string | string[];
	};
}

const props = defineProps<IPopOverProps>();
const container: Ref<HTMLDivElement | null> = ref(null);
const emit = defineEmits(["close", "clickout", "opened"]);

watchEffect(() => {
	if (props.modelValue) {
		emit("opened");
	}
});

const clickout = (event: MouseEvent) => {
	if (event.target && !container.value?.contains(event.target as HTMLElement)) {
		emit("clickout");
	}
};

const onAfterEnter = () => {
	document.body.classList.add("scroll-lock");
	if (!isMobile) {
		document.addEventListener("click", clickout);
	}
};

const onAfterLeave = () => {
	document.body.classList.remove("scroll-lock");
	if (!isMobile) {
		document.removeEventListener("click", clickout);
	}
};
</script>

<template>
	<div :class="styles?.wrapper ?? 'relative'" ref="container">
		<div>
			<slot name="trigger"></slot>
		</div>
		<Teleport :to="teleportElement ?? container" v-if="!isMobile && container">
			<Transition name="fade" @after-enter="onAfterEnter" @after-leave="onAfterLeave" appear>
				<div v-if="modelValue && !isMobile" :class="styles?.modelWrapper ?? 'absolute top-0 left-0 w-full z-50'">
					<div :class="styles?.content ?? 'bg-light-50 rounded-2xl overflow-hidden h-full flex flex-col'">
						<slot name="header">
							<div v-if="title" :class="styles?.header ?? 'text-light-50 p-2 bg-dark-900 flex justify-between font-bold'">
								{{ title }}
								<UiIcon :path="mdiClose" class="w-6 h-6 cursor-pointer top-2 right-2 fill-light-50" @click="emit('close')" />
							</div>
						</slot>
						<div :class="styles?.contentInner ?? 'p-2 bg-light-200 dark:bg-dark-800 drop-shadow-sm overflow-y-auto h-[max-content]'">
							<slot></slot>
						</div>
					</div>
				</div>
			</Transition>
		</Teleport>

		<UiModal
			:title="title"
			:active="modelValue"
			@set-active="() => emit('close')"
			size="small"
			rootClass="z-[100] text-xs leading-4"
			:contentClass="styles?.modalContent"
			:disableClose="disableClose ? disableClose : false"
			v-else
		>
			<template #titleElement>
				<span class="px-2">{{ title }}</span>
			</template>
			<slot></slot>
		</UiModal>
	</div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
